'use client';
import classNames from 'classnames';
import React, { useCallback, useEffect, useId, useRef, useState } from 'react';

export type SortByDropdownOption = {
  value: string;
  label: string;
  /** Second line under the label; rendered only when the editor authored one. */
  helpText?: string;
};

export type SortByDropdownProps = {
  options: SortByDropdownOption[];
  value?: string;
  /**
   * What the server rendered — the default option, since it cannot read
   * `cms_sort`. Used for the first client render only, so hydration matches.
   */
  ssrValue?: string;
  /**
   * False for an instance that mounts after hydration (the drawer, the sheet):
   * with no served HTML to match it must paint `value` on its first frame.
   */
  rendersOnServer?: boolean;
  /** Id of the visible "SORT BY" heading, which labels the control. */
  labelId: string;
  /** Shown when nothing is selected. */
  placeholder: string;
  /** A committed choice: pushes history and is reported to analytics. */
  onChange: (value: string) => void;
  /**
   * An intermediate selection while arrowing: reorders and rewrites the URL in
   * place, with no history entry and no analytics event.
   */
  onPreview?: (value: string) => void;
};

/**
 * A listbox rather than a native `<select>`, whose option list renders in the OS
 * font. Local rather than the shared `Select`, which the car-insurance forms use.
 */
export const SortByDropdown: React.FC<SortByDropdownProps> = ({
  options,
  value,
  ssrValue,
  rendersOnServer = true,
  labelId,
  placeholder,
  onChange,
  onPreview,
}) => {
  const [open, setOpen] = useState(false);
  // False for the first client render so the trigger's label matches the served
  // HTML; the effect then swaps in the label the URL asks for.
  const [hydrated, setHydrated] = useState(!rendersOnServer);
  useEffect(() => setHydrated(true), []);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const triggerLabelId = useId();
  const optionIdBase = useId();
  const listboxId = useId();
  const optionId = (optionValue: string) => `${optionIdBase}-${optionValue}`;

  // The capped list can outgrow `max-h-60` in a locale with longer labels;
  // `block: 'nearest'` is inert until an option is actually out of view.
  useEffect(() => {
    if (!open || !value) return;
    // Optional call: jsdom has no layout, so `scrollIntoView` is absent there.
    document.getElementById(optionId(value))?.scrollIntoView?.({
      block: 'nearest',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, value]);

  const selectedIndex = options.findIndex((option) => option.value === value);
  const selected = selectedIndex >= 0 ? options[selectedIndex] : undefined;
  // What this render paints as active. The list is served too — hidden — so it
  // has to reconcile against the same value the trigger does.
  const paintedValue = hydrated ? value : ssrValue;
  const labelOption = options.find((option) => option.value === paintedValue);

  const close = useCallback((refocus = false) => {
    setOpen(false);
    if (refocus) triggerRef.current?.focus();
  }, []);

  // Readable from the document listeners below without re-subscribing per preview.
  const selectedRef = useRef(selected);
  selectedRef.current = selected;
  // Whether dismissing the list still owes a commit for an arrowed-to choice.
  const commitOnClose = useRef(false);

  const commitIfPending = useCallback(() => {
    if (!commitOnClose.current) return;
    const pending = selectedRef.current;
    commitOnClose.current = false;
    if (pending) onChange(pending.value);
  }, [onChange]);

  // `focusin` covers tabbing away, which the pointer listeners cannot see; it is
  // scoped to the root, so tabbing into an option keeps the list open.
  useEffect(() => {
    if (!open) return;

    const closeIfOutside = (event: Event) => {
      if (rootRef.current?.contains(event.target as Node)) return;
      commitIfPending();
      setOpen(false);
    };

    document.addEventListener('mousedown', closeIfOutside);
    document.addEventListener('touchstart', closeIfOutside);
    document.addEventListener('focusin', closeIfOutside);

    return () => {
      document.removeEventListener('mousedown', closeIfOutside);
      document.removeEventListener('touchstart', closeIfOutside);
      document.removeEventListener('focusin', closeIfOutside);
    };
  }, [open, commitIfPending]);

  // Arrowing previews rather than commits: a commit per keystroke costs a history
  // entry and an analytics event for every step of one decision.
  const preview = (optionValue: string) => {
    if (onPreview) {
      commitOnClose.current = true;
      onPreview(optionValue);
      return;
    }

    // No preview channel means the arrow key already committed, so the dismissal
    // must not commit again.
    commitOnClose.current = false;
    onChange(optionValue);
  };

  const select = (optionValue: string) => {
    commitOnClose.current = false;
    onChange(optionValue);
    close(true);
  };

  // The arrowed-to value is already applied, so the decision is still owed once.
  const closeAndCommit = (refocus = false) => {
    commitIfPending();
    close(refocus);
  };

  const step = (delta: number) => {
    if (!options.length) return;

    // No selection has no position to step from: Down lands on the first option,
    // Up on the last.
    if (selectedIndex < 0) {
      preview(options[delta > 0 ? 0 : options.length - 1].value);
      return;
    }

    const next = Math.min(
      Math.max(selectedIndex + delta, 0),
      options.length - 1,
    );
    if (next !== selectedIndex) preview(options[next].value);
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    switch (event.key) {
      case 'Escape':
        if (open) {
          event.preventDefault();
          closeAndCommit(true);
        }
        break;
      case 'Enter':
      case ' ':
        if (open && selected) {
          event.preventDefault();
          select(selected.value);
        }
        break;
      case 'ArrowDown':
      case 'ArrowUp':
        event.preventDefault();
        if (!open) {
          setOpen(true);
        } else {
          step(event.key === 'ArrowDown' ? 1 : -1);
        }
        break;
      case 'Home':
      case 'End':
        if (open && options.length) {
          event.preventDefault();
          preview(options[event.key === 'Home' ? 0 : options.length - 1].value);
        }
        break;
      default:
        break;
    }
  };

  return (
    <div ref={rootRef} className="relative">
      <button
        ref={triggerRef}
        type="button"
        // Not the implicit `button`: `aria-activedescendant` is only valid on a
        // role that supports it, and focus stays here while the list is open.
        role="combobox"
        aria-haspopup="listbox"
        // Permanent, as the combobox pattern expects: the listbox stays mounted
        // and is hidden while collapsed, so the id always resolves.
        aria-controls={listboxId}
        aria-expanded={open}
        // Focus stays on the trigger, so this is what names the option the arrow
        // keys move to.
        aria-activedescendant={
          open && selected ? optionId(selected.value) : undefined
        }
        aria-labelledby={`${labelId} ${triggerLabelId}`}
        // Collapsing here is a dismissal and owes the same commit; the document
        // `mousedown` listener cannot cover it, being scoped outside `rootRef`.
        onClick={() => (open ? closeAndCommit() : setOpen(true))}
        onKeyDown={onKeyDown}
        className={classNames(
          'mhg-alpha-typography-body-2 flex w-full items-center justify-between gap-spacing/gap/x-xs',
          'rounded-radius/button border border-borders/form-default bg-fill/card p-3 text-left font-semibold',
          'text-text/primary hover:bg-fill/alternative-1',
          'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-fill/primary',
        )}
      >
        <span
          id={triggerLabelId}
          className={classNames('truncate', {
            'text-text/secondary-lighter': !labelOption,
          })}
        >
          {labelOption?.label ?? placeholder}
        </span>
        <svg
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
          focusable="false"
          className={classNames(
            'size-4 shrink-0 text-text/secondary-lighter transition-transform duration-150',
            { 'rotate-180': open },
          )}
        >
          <path
            d="M6 9l6 6 6-6"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {/* Buttons rather than clickable `li`s, so each option is a real
          interactive element; `role="option"` keeps the listbox valid. */}
      <div
        id={listboxId}
        role="listbox"
        // The attribute is for the accessibility tree; the class is what hides
        // it. `[hidden]` comes from preflight's base layer and loses to a
        // `flex` utility of equal specificity declared later.
        hidden={!open}
        aria-labelledby={labelId}
        className={classNames(
          open ? 'flex' : 'hidden',
          'mhg-alpha-typography-body-2 max-h-60 flex-col overflow-y-auto rounded-radius/button border border-borders/form-default bg-fill/card py-1 shadow-md',
          'mt-1',
          // In flow on mobile: the panel is a bottom sheet against the bottom
          // of the screen, so an absolute list opening downwards is clipped.
          'md:absolute md:inset-x-0 md:top-full md:z-10',
        )}
      >
        {options.map((option) => {
          const isSelected = option.value === paintedValue;

          return (
            <button
              key={option.value}
              id={optionId(option.value)}
              type="button"
              role="option"
              aria-selected={isSelected}
              // Out of the tab order: the keys are handled on the trigger, so a
              // tabbable option has nothing above it to handle them.
              tabIndex={-1}
              onClick={() => select(option.value)}
              className={classNames(
                'mhg-alpha-typography-body-2 px-3 py-2 text-left',
                // No hover on the active option: it must keep the brand colour
                // rather than grey out under the pointer.
                isSelected
                  ? 'bg-fill/primary-light font-bold text-fill/primary-dark'
                  : 'text-text/primary hover:bg-fill/alternative-1',
              )}
            >
              {option.label}
              {/* CMS-1893 R6.2: the editor's help text is a second line under
                  the label, not a tooltip. Only rendered when authored. */}
              {option.helpText && (
                <span className="mhg-alpha-typography-caption block font-normal text-text/secondary">
                  {option.helpText}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};
